import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Grid,
  CircularProgress,
  Divider,
  useTheme
} from '@mui/material';
import { Close as X, LocationOn as MapPin, Phone, Store, AddCircle } from '@mui/icons-material';
import apiClient from '../../../../../api/client';

// Styled Dialog for consistent styling
const StyledDialog = styled(Dialog)(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  
  return {
    '& .MuiDialog-paper': {
      borderRadius: '16px',
      boxShadow: '0 24px 48px rgba(0, 0, 0, 0.2)',
      background: isDarkMode 
        ? 'linear-gradient(135deg, rgba(30, 30, 30, 0.98), rgba(42, 42, 42, 0.98))'
        : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(240, 245, 255, 0.98))',
      backdropFilter: 'blur(10px)',
      overflow: 'hidden',
      [theme.breakpoints.down('sm')]: {
        margin: '16px',
        width: 'calc(100% - 32px)',
        maxWidth: '100%',
      }
    }
  };
});

const DialogHeader = styled(DialogTitle)(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  
  return {
    background: isDarkMode 
      ? 'linear-gradient(135deg, #4a5568 0%, #553c8b 100%)'
      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: theme.spacing(2, 3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };
});

const DialogSubtitle = styled(Typography)(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  
  return {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: isDarkMode ? '#ffffff' : '#4a5568',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  };
});

const StyledTextField = styled(TextField)(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  
  return {
    marginBottom: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      transition: 'all 0.2s ease',
      backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
      color: isDarkMode ? '#ffffff' : 'inherit',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.23)',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#667eea',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#667eea',
        borderWidth: '2px',
      }
    },
    '& .MuiInputLabel-root': {
      color: isDarkMode ? '#b3b3b3' : 'inherit',
      '&.Mui-focused': {
        color: '#667eea',
      }
    },
    '& .MuiFormHelperText-root': {
      color: isDarkMode ? '#b3b3b3' : 'inherit',
    },
    '& .MuiInputAdornment-root': {
      marginRight: theme.spacing(1),
    }
  };
});

const AnimatedButton = styled(Button)(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  
  return {
    background: isDarkMode 
      ? 'linear-gradient(135deg, #4a5568 0%, #553c8b 100%)'
      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    borderRadius: '12px',
    padding: '10px 24px',
    fontWeight: 600,
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)',
    '&:hover': {
      background: isDarkMode 
        ? 'linear-gradient(135deg, #3a4454 0%, #453177 100%)'
        : 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 16px rgba(102, 126, 234, 0.3)',
    },
    '&:disabled': {
      background: '#a0aec0',
      boxShadow: 'none',
      color: 'white',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
      transition: 'all 0.5s ease',
    },
    '&:hover::before': {
      left: '100%',
    }
  };
});

const CancelButton = styled(Button)(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  
  return {
    borderRadius: '12px',
    padding: '9px 22px',
    fontWeight: 600,
    transition: 'all 0.2s ease',
    color: isDarkMode ? '#ffffff' : '#667eea',
    borderColor: isDarkMode ? '#404040' : 'rgba(102, 126, 234, 0.5)',
    '&:hover': {
      borderColor: '#667eea',
      backgroundColor: isDarkMode 
        ? 'rgba(102, 126, 234, 0.1)' 
        : 'rgba(102, 126, 234, 0.05)',
      transform: 'translateY(-1px)',
    }
  };
});

const ErrorBox = styled(Box)(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  
  return {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: isDarkMode 
      ? 'rgba(244, 67, 54, 0.2)' 
      : 'rgba(244, 67, 54, 0.08)',
    borderRadius: theme.shape.borderRadius,
    color: isDarkMode ? '#ffffff' : '#f44336',
    border: `1px solid ${isDarkMode ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.2)'}`,
  };
});

/**
 * Component for adding a new branch
 */
const AddNewBranch = ({ open, onClose, onBranchAdded }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  const [branchData, setBranchData] = useState({
    location_name: '',
    address: '',
    contact_number: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!branchData.location_name.trim()) {
      newErrors.location_name = t('vendor.addBranch.branchNameRequired');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await apiClient.post('/api/v1/users/create-branch', branchData);
      
      onBranchAdded(response.data);
      
      // Clear form
      setBranchData({
        location_name: '',
        address: '',
        contact_number: ''
      });
      setErrors({});
      
    } catch (error) {
      console.error('Error creating branch:', error);
      
      // Handle validation errors from the backend
      if (error.response?.data?.details) {
        setErrors(prev => ({
          ...prev,
          apiError: error.response.data.message || t('vendor.addBranch.createError')
        }));
      } else {
        setErrors(prev => ({
          ...prev,
          apiError: t('vendor.addBranch.createError')
        }));
      }
      
    } finally {
      setLoading(false);
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBranchData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };
  
  return (
    <StyledDialog 
      open={open} 
      onClose={loading ? null : onClose}
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <DialogHeader>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddCircle fontSize="small" />
            <Typography variant="h6" component="div" sx={{ fontWeight: 600, fontSize: '1.1rem', color: 'white' }}>
              {t('vendor.addBranch.title')}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: 'white' }} disabled={loading}>
            <X fontSize="small" />
          </IconButton>
        </DialogHeader>
        
        <DialogContent sx={{ 
          p: 3, 
          pt: 4,
          backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff'
        }}>
          {errors.apiError && (
            <ErrorBox>
              <Typography variant="body2" fontWeight={500}>
                {errors.apiError}
              </Typography>
            </ErrorBox>
          )}
          
          <DialogSubtitle>
            {t('vendor.addBranch.branchDetails')}
          </DialogSubtitle>
          <Divider sx={{ 
            mb: 3,
            borderColor: isDarkMode ? '#404040' : 'inherit'
          }} />
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <StyledTextField
                autoFocus
                name="location_name"
                label={t('vendor.addBranch.branchName')}
                fullWidth
                value={branchData.location_name}
                onChange={handleInputChange}
                required
                variant="outlined"
                placeholder={t('vendor.addBranch.branchNamePlaceholder')}
                error={!!errors.location_name}
                helperText={errors.location_name || t('vendor.addBranch.branchNameHelper')}
                InputProps={{
                  startAdornment: (
                    <Store fontSize="small" sx={{ color: '#667eea', mr: 1 }} />
                  ),
                }}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12}>
              <StyledTextField
                name="address"
                label={t('vendor.addBranch.address')}
                fullWidth
                value={branchData.address}
                onChange={handleInputChange}
                variant="outlined"
                placeholder={t('vendor.addBranch.addressPlaceholder')}
                multiline
                rows={2}
                InputProps={{
                  startAdornment: (
                    <MapPin fontSize="small" sx={{ color: '#667eea', mr: 1, mt: 1 }} />
                  ),
                }}
                helperText={t('vendor.addBranch.addressHelper')}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12}>
              <StyledTextField
                name="contact_number"
                label={t('vendor.addBranch.contactNumber')}
                fullWidth
                value={branchData.contact_number}
                onChange={handleInputChange}
                variant="outlined"
                placeholder={t('vendor.addBranch.contactPlaceholder')}
                InputProps={{
                  startAdornment: (
                    <Phone fontSize="small" sx={{ color: '#667eea', mr: 1 }} />
                  ),
                }}
                helperText={t('vendor.addBranch.contactHelper')}
                disabled={loading}
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <Box sx={{ 
          px: 3, 
          py: 0.5, 
          backgroundColor: isDarkMode 
            ? 'rgba(102, 126, 234, 0.1)' 
            : 'rgba(102, 126, 234, 0.03)',
          borderTop: `1px solid ${isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.12)'}`
        }}>
          <Divider sx={{ 
            borderColor: isDarkMode ? '#404040' : 'inherit'
          }} />
        </Box>
        
        <DialogActions sx={{ 
          p: 3, 
          pt: 2, 
          pb: 3,
          backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff'
        }}>
          <CancelButton 
            onClick={onClose} 
            variant="outlined"
            disabled={loading}
          >
            {t('common.cancel')}
          </CancelButton>
          <AnimatedButton 
            type="submit"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} color="inherit" />}
          >
            {loading ? t('vendor.addBranch.adding') : t('vendor.addBranch.addButton')}
          </AnimatedButton>
        </DialogActions>
      </form>
    </StyledDialog>
  );
};

export default AddNewBranch;